import { useContext, useEffect, useRef } from 'react';

import { StudioResumeContext } from './studio-resume-context';

import type { StudioVisitInput } from './studio-resume';

export type { StudioVisit, StudioVisitInput } from './studio-resume';

export function useStudioResume() {
  return useContext(StudioResumeContext);
}

/** Readiness may arrive asynchronously; only a foreground route visit advances resume memory. */
export function useRecordStudioVisit(
  visit: StudioVisitInput | null,
  { entryId, visible = true }: { entryId: string; visible?: boolean },
) {
  const { remember } = useStudioResume();
  const recorded = useRef<string | null>(null);
  const entry = useRef<string | null>(null);
  const programSlug = visit?.programSlug;
  const activityId = visit?.activityId;
  const kind = visit?.kind;
  useEffect(() => {
    if (entry.current !== entryId || !visible) {
      entry.current = entryId;
      recorded.current = null;
    }
    if (!visible) return;
    const record = () => {
      if (document.visibilityState === 'hidden') {
        recorded.current = null;
        return;
      }
      if (!programSlug || !activityId || !kind) return;
      const identity = `${programSlug}:${entryId}:${kind}`;
      if (recorded.current === identity) return;
      recorded.current = identity;
      remember({ programSlug, activityId, kind });
    };
    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, [remember, programSlug, activityId, kind, entryId, visible]);
}
